import {FC, useState, useEffect, useRef} from 'react';
//mui
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import FilterMenu from '@components/Table/FilterMenu';
import ActionsMenu from '@components/Table/ActionMenu';
import Button from '@components/Button';
import Input from '@components/Inputs/Input';
import Modal from '@components/Modal';
import Select from '@components/Select';
//images
import organization from '@images/organization-example.png';
//icons
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import {ReactComponent as SearchIcon} from '@icons/search-normal.svg';
//constants
import {
  BODY1,
  BODY2,
  CREATE,
  ADD_TEAM_MEMBER,
  DD_MM_YYYY_HH_MM_DOT,
  DELETE,
  DELETE_CONTACT,
  DELETE_CONTACT_DESCRIPTION,
  ERROR,
  LAST_ACTIVE,
  MEMBER,
  ROLE,
  SUBTITLE1,
  SUBTITLE2,
  TEAM_MEMBER,
  EDIT,
  SAVE,
  EDIT_TEAM_MEMBER,
} from '@/utils/constants';
//components
import CreateTeamMemberForm from './CreateTeamMemberForm';
import TeamFilterContent from '@/shared/components/Table/FilterMenu/FilterContent';
//helpers
import {defaultFilterOptions, options, updateTeamFilterDataOptions} from './helpers';
import {FilterActivity, FilterRole, IFilterGroup, ITeamData, SelectOptionsType} from '@/utils/types';
//style
import {teamSectionStyle} from './Team.styles';
import {useAppDispatch} from '@/store/hooks';
import {
  createTeamMember,
  deleteTeamMember,
  getTeamDataSelector,
  getTeamMembers,
  updateMemberRole,
} from '@/store/orgatization-team';
import {useSelector} from 'react-redux';
import {CreateTeamMemberFormValues, initialValues, validationSchema} from './CreateTeamMemberForm/formConfiguration';
import {Formik, FormikProps} from 'formik';
import {ITeamRole} from '@/utils/enums';
import {getErrorMessage} from '@/utils/helpers';
import {toast} from 'react-toastify';
import {OrganizationTeamService, orgStorage} from '@/utils/services';
import Loader from '@/shared/components/Loader';
import Pagination from '@/shared/components/Pagination';
import {getOrganizationSelector} from '@/store/organization';
import EmptyTableData from '@/shared/components/Table/EmptyTableData';
import dayjs from 'dayjs';
import EditTeamMemberForm from './EditTeamMemberForm';
import {EditTeamMemberFormValues, editValidationSchema} from './EditTeamMemberForm/formConfiguration';
import { useApiInstance } from '@/contexts/ApiInstanceContext';

const ProfileTeam: FC = () => {
  const organizationId = orgStorage.get('organizationId');
  const teamMembers = useSelector(getTeamDataSelector);
  const organizationData = useSelector(getOrganizationSelector);

  const [isLoading, setIsLoading] = useState(false);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState(false);
  const [isShowCreateTeamMemberModal, setIsShowCreateTeamMemberModal] = useState(false);
  const [isShowEditTeamMemberModal, setIsShowEditTeamMemberModal] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [role, setRole] = useState(defaultFilterOptions[0].options[0].value);
  const [activity, setActivity] = useState(defaultFilterOptions[1].options[0].value);
  const [filterData, setFilterData] = useState<IFilterGroup[]>(defaultFilterOptions);
  const [memberToDelete, setMemberToDelete] = useState<number | null>(null);
  const [memberToEdit, setMemberToEdit] = useState<ITeamData | null>(null);
  const [page, setPage] = useState(1);

  const pageSize = 9;
  const totalCount = teamMembers.totalCount ?? 0;

  const dispatch = useAppDispatch();
  const formikRef = useRef<FormikProps<CreateTeamMemberFormValues>>(null);
  const editFormikRef = useRef<FormikProps<EditTeamMemberFormValues>>(null);

  const {apiInstance} = useApiInstance();

  const editInitialValues = {
    firstName: memberToEdit?.firstName ?? '',
    lastName: memberToEdit?.lastName ?? '',
    email: memberToEdit?.email ?? '',
    logo: memberToEdit?.logo ?? null,
  };

  const onSubmitClickHandler = () => {
    formikRef.current?.handleSubmit();
  };

  const onEditSubmitClickHandler = () => {
    editFormikRef.current?.handleSubmit();
  };

  const updateRole = async (_: any, role: SelectOptionsType, id: number | undefined) => {
    if (!id) return;

    try {
      const data = {id, role: role.name === ITeamRole.MEMBER ? ITeamRole.STANDART : (role.name as ITeamRole)};

      await dispatch(updateMemberRole(data)).unwrap();
      await getTeamList();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCloseTeamCreateModal = () => {
    formikRef.current?.resetForm();
    setIsShowCreateTeamMemberModal(false);
  };

  const handleOpenEditModal = (row: ITeamData) => {
    setMemberToEdit(row);
    setIsShowEditTeamMemberModal(true);
  };

  const handleCloseEditModal = () => {
    editFormikRef.current?.resetForm();
    setMemberToEdit(null);
    setIsShowEditTeamMemberModal(false);
  };

  const getLastActiveSx = (status: string) => {
    if (status.includes('Active')) return teamSectionStyle.lastActiveDate;
    if (status.includes('Invited')) return teamSectionStyle.lastActiveInvited;
    return teamSectionStyle.lastActiveDate;
  };

  const findRoleIndexByName = (roleName: ITeamRole) => {
    const role = options.find((option) =>
      roleName === ITeamRole.STANDART ? option.name === ITeamRole.MEMBER : option.name === roleName
    );

    return role?.id.toString() ?? '';
  };

  const handleFilterChange = (groupTitle: string, optionTitle: string) => {
    setPage(1);
    if (groupTitle === 'Role') setRole(optionTitle);
    else setActivity(optionTitle);
  };

  useEffect(() => {
    setFilterData(updateTeamFilterDataOptions(role!, activity!, filterData));
  }, [role, activity]);

  const handleDeleteTeamMember = async () => {
    if (!memberToDelete) return;
    setIsLoading(true);

    try {
      await dispatch(deleteTeamMember(memberToDelete)).unwrap();
      await getTeamList();
      setMemberToDelete(null);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsShowDeleteModal(false);
      setIsLoading(false);
    }
  };

  const handleCreateTeamMember = async (values: CreateTeamMemberFormValues) => {
    if (!organizationId) return;

    const memberDto = {
      firstName: values.firstName,
      lastName: values.lastName,
      userName: values.userName,
      organizationId: +organizationId,
      password: values.password,
      role: values.role === ITeamRole.MEMBER ? ITeamRole.STANDART : (values.role as ITeamRole),
      email: values.emailForPassword,
      logo: null,
    };

    try {
      await dispatch(createTeamMember(memberDto)).unwrap();
      setIsShowCreateTeamMemberModal(false);
      await getTeamList();

      formikRef.current?.resetForm();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSubmitUpdateTeamMember = async (values: EditTeamMemberFormValues) => {
    if (!organizationId) return;

    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
	  ...(values.logo && {logo: values?.logo?.split('/')[4].split('?')[0]}),
    };

    try {
      await OrganizationTeamService.updateTeamMember(memberToEdit?.id as number, data);
      await getTeamList();
      setIsShowEditTeamMemberModal(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  const getTeamList = async (value?: string) => {
    setIsLoading(true);

    const memberDto = {
      page,
      pageSize,
      searchName: value || '',
      role: role! as FilterRole,
      activity: activity! as FilterActivity,
      organizationId: +organizationId!,
    };

    try {
      await dispatch(getTeamMembers({reqBody: memberDto, apiInstance})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    setPage(1);
    getTeamList(value);
  };

  useEffect(() => {
    if (!organizationId) return;

    getTeamList();
  }, [activity, role, page]);

  const handleResendInvite = async (id: number) => {
    try {
      await OrganizationTeamService.resendInvite(id);
      toast.success('Invite has been sent');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Box sx={teamSectionStyle.wrapper}>
      <Box sx={teamSectionStyle.tableWrapper}>
        <Box sx={teamSectionStyle.filterRow}>
          <Typography variant={SUBTITLE1} sx={teamSectionStyle.title}>
            {TEAM_MEMBER}
          </Typography>
          <Box>
            <FilterMenu content={<TeamFilterContent options={filterData} onFilterChange={handleFilterChange} />} />
          </Box>
          <Box sx={teamSectionStyle.search}>
            <SearchIcon />
            <Input placeholder="Search team members" value={searchValue} onChange={handleSearch} />
          </Box>
          <Box>
            <Button onClick={() => setIsShowCreateTeamMemberModal(true)} title={ADD_TEAM_MEMBER} />
          </Box>
        </Box>
        {isLoading ? (
          <Loader sx={teamSectionStyle.loader} />
        ) : teamMembers.data.length === 0 ? (
          <EmptyTableData />
        ) : (
          <Box sx={teamSectionStyle.tableWrapper}>
            <TableContainer sx={teamSectionStyle.tableContainer}>
              <Table sx={{minWidth: 650}} aria-label="simple table">
                <TableHead>
                  <TableRow sx={{height: '32px'}}>
                    <TableCell sx={teamSectionStyle.avatarHeadRow} />
                    <TableCell> {MEMBER}</TableCell>
                    <TableCell sx={teamSectionStyle.lastActiveHeadRow}>{LAST_ACTIVE}</TableCell>
                    <TableCell sx={teamSectionStyle.roleHeadRow}>{ROLE}</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {teamMembers.data.length &&
                    teamMembers.data.map((row) => (
                      <TableRow sx={teamSectionStyle.avatarBodyRow} key={row.id}>
                        <TableCell>
                          <Box>
                            <IconButton>
                              <img
                                src={row.logo || organization}
                                alt="avatar"
                                style={{borderRadius: '50%', width: '44px', height: '44px'}}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant={SUBTITLE1} sx={teamSectionStyle.name}>
                              {row.role === 'Owner' ? organizationData?.name : `${row.firstName} ${row.lastName}`}
                            </Typography>
                          </Box>
                          <Typography variant={BODY2} sx={teamSectionStyle.email}>
                            {row.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={getLastActiveSx(row.status)}>
                            {row.lastActive
                              ? dayjs.utc(row.lastActive).local().format(DD_MM_YYYY_HH_MM_DOT)
                              : 'Invitation sent'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={teamSectionStyle.roleBodyRow}>
                          {row.role !== 'Owner' ? (
                            <Select
                              options={options}
                              value={findRoleIndexByName(row.role as ITeamRole)}
                              id={row.id}
                              onChange={updateRole}
                            />
                          ) : (
                            <Typography variant={SUBTITLE2} sx={teamSectionStyle.owner}>
                              {row.role}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={teamSectionStyle.menu}>
                          {row.role === 'Owner' ? null : (
                            <ActionsMenu
                              content={
                                <Box sx={teamSectionStyle.menuContent}>
                                  <Typography
                                    sx={teamSectionStyle.text}
                                    onClick={() => {
                                      handleOpenEditModal(row);
                                    }}
                                    variant={BODY1}
                                  >
                                    {EDIT}
                                  </Typography>
                                  {row.status === 'Invited' && (
                                    <Typography
                                      sx={teamSectionStyle.text}
                                      onClick={() => handleResendInvite(row.id)}
                                      variant={BODY1}
                                    >
                                      Resend invite
                                    </Typography>
                                  )}
                                  <Typography
                                    sx={teamSectionStyle.text}
                                    onClick={() => {
                                      setMemberToDelete(row.id);
                                      setIsShowDeleteModal(true);
                                    }}
                                    variant={BODY1}
                                  >
                                    {DELETE}
                                  </Typography>
                                </Box>
                              }
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={teamSectionStyle.pagination}>
              <Pagination page={page} pageSize={pageSize} total={totalCount} onChange={(step) => setPage(step)} />
            </Box>
          </Box>
        )}

        <Modal
          onClose={() => setIsShowDeleteModal(false)}
          onSave={handleDeleteTeamMember}
          open={isShowDeleteModal}
          sx={teamSectionStyle.deleteModal}
          color={ERROR}
          mainButtonTitle={DELETE}
          isShowCloseButton
          isShowSaveButton
          mainIcon={<DangerIcon />}
          title={DELETE_CONTACT}
          description={DELETE_CONTACT_DESCRIPTION}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleCreateTeamMember}
          innerRef={formikRef}
        >
          <Modal
            onClose={handleCloseTeamCreateModal}
            onSave={onSubmitClickHandler}
            open={isShowCreateTeamMemberModal}
            sx={teamSectionStyle.createMemberModal}
            mainButtonTitle={CREATE}
            isShowCloseButton
            isShowSaveButton
            title={ADD_TEAM_MEMBER}
          >
            <CreateTeamMemberForm />
          </Modal>
        </Formik>
        <Formik
          initialValues={editInitialValues}
          validationSchema={editValidationSchema}
          onSubmit={handleSubmitUpdateTeamMember}
          innerRef={editFormikRef}
          enableReinitialize
        >
          <Modal
            onClose={handleCloseEditModal}
            onSave={onEditSubmitClickHandler}
            open={isShowEditTeamMemberModal}
            sx={teamSectionStyle.updateMemberModal}
            mainButtonTitle={SAVE}
            isShowCloseButton
            isShowSaveButton
            title={EDIT_TEAM_MEMBER}
          >
            <EditTeamMemberForm />
          </Modal>
        </Formik>
      </Box>
    </Box>
  );
};

export default ProfileTeam;
